import { css } from "@linaria/core";

import { MOBILE_BREAKPOINT_PX, TABLET_BREAKPOINT_PX } from "./constants";
import { breakpointDownMixin } from "./mixins/breakpoints";
import { resetAnchorColorProp } from "./resets";

const colorPrefix = "--color";
export const colorProps = {
  bg: `${colorPrefix}--bg`,
  shadow: `${colorPrefix}--shadow`,
  grey_50: `${colorPrefix}--grey--50`,
  grey_100: `${colorPrefix}--grey--100`,
  grey_400: `${colorPrefix}--grey--400`,
  text: `${colorPrefix}--text`,
  textInversed: `${colorPrefix}--text-inversed`,
  primary: `${colorPrefix}--primary`,
  brand: `${colorPrefix}--brand`,
  textOnBrand: `${colorPrefix}--text--on-brand`,
};

const figtreePrefix = "--figtree";
export const figtreeProps = {
  fontFamily: `${figtreePrefix}--family`,
  regular: `${figtreePrefix}--font-weight--regular`,
  medium: `${figtreePrefix}--font-weight--medium`,
  bold: `${figtreePrefix}--font-weight--bold`,
};

const easingPrefix = "--ease";
export const easingProps = {
  inQuad: `${easingPrefix}--in-quad`,
  outQuad: `${easingPrefix}--out-quad`,
  inOutQuad: `${easingPrefix}--in-out-quad`,
  inCubic: `${easingPrefix}--in-cubic`,
  outCubic: `${easingPrefix}--out-cubic`,
  inOutCubic: `${easingPrefix}--in-out-cubic`,
  inQuart: `${easingPrefix}--in-quart`,
  outQuart: `${easingPrefix}--out-quart`,
  inOutQuart: `${easingPrefix}--in-out-quart`,
  inOutQuint: `${easingPrefix}--in-out-quint`,
  inSine: `${easingPrefix}--in-sine`,
  outSine: `${easingPrefix}--out-sine`,
  elastic: `${easingPrefix}--elastic`,
};

export const boundaryPaddingProp = "--boundary--padding";
export const headerPaddingProp = "--header--padding";
export const blockMarginProp = "--block--margin";

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
css`
  :global(:root) {
    ${colorProps.bg}: 255, 255, 255;
    ${colorProps.shadow}: 0, 0, 0;
    ${colorProps.grey_50}: 247, 247, 247;
    ${colorProps.grey_100}: 232, 232, 232;
    ${colorProps.grey_400}: 112, 112, 112;
    ${colorProps.text}: 34, 34, 34;
    ${colorProps.textInversed}: var(${colorProps.bg});
    ${colorProps.primary}: 11, 88, 216;
    ${colorProps.brand}: var(${colorProps.primary});
    ${colorProps.textOnBrand}: var(${colorProps.text});
    ${figtreeProps.fontFamily}: figtree, helvetica, sans-serif;
    ${figtreeProps.regular}: 400;
    ${figtreeProps.medium}: 600;
    ${figtreeProps.bold}: 700;
    ${easingProps.inQuad}: cubic-bezier(0.11, 0, 0.5, 0);
    ${easingProps.outQuad}: cubic-bezier(0.5, 1, 0.89, 1);
    ${easingProps.inOutQuad}: cubic-bezier(0.455, 0.03, 0.515, 0.955);
    ${easingProps.inCubic}: cubic-bezier(0.55, 0.055, 0.675, 0.19);
    ${easingProps.outCubic}: cubic-bezier(0.33, 1, 0.68, 1);
    ${easingProps.inOutCubic}: cubic-bezier(0.645, 0.045, 0.355, 1);
    ${easingProps.inQuart}: cubic-bezier(0.895, 0.03, 0.685, 0.22);
    ${easingProps.outQuart}: cubic-bezier(0.165, 0.84, 0.44, 1);
    ${easingProps.inOutQuart}: cubic-bezier(0.77, 0, 0.175, 1);
    ${easingProps.inOutQuint}: cubic-bezier(0.86, 0, 0.07, 1);
    ${easingProps.inSine}: cubic-bezier(0.47, 0, 0.745, 0.715);
    ${easingProps.outSine}: cubic-bezier(0.39, 0.575, 0.565, 1);
    ${easingProps.elastic}: cubic-bezier(0.4, 1.01, 0.32, 1.05);
    ${resetAnchorColorProp}: var(${colorProps.text});
    ${headerPaddingProp}: 6rem;
    ${boundaryPaddingProp}: 4.5rem;
    ${blockMarginProp}: 6rem;

    @media screen and (${breakpointDownMixin(TABLET_BREAKPOINT_PX)}) {
      ${boundaryPaddingProp}: 3rem;
    }

    @media screen and (${breakpointDownMixin(MOBILE_BREAKPOINT_PX)}) {
      ${headerPaddingProp}: 30px;
      ${boundaryPaddingProp}: 30px;
      ${blockMarginProp}: 4.5rem;
    }
  }

  /* latin-ext */
  @font-face {
    font-family: "figtree";
    font-weight: 300 900;
    font-style: italic;

    src: url("/public/fonts/figtree-latin-italic-ext.woff2") format("woff2");
    unicode-range:
      U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304,
      U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020,
      U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }

  /* latin */
  @font-face {
    font-family: "figtree";
    font-weight: 300 900;
    font-style: italic;

    src: url("/public/fonts/figtree-latin-italic.woff2") format("woff2");
    unicode-range:
      U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC,
      U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193,
      U+2212, U+2215, U+FEFF, U+FFFD;
  }

  /* latin-ext */
  @font-face {
    font-family: "figtree";
    font-weight: 300 900;
    font-style: normal;

    src: url("/public/fonts/figtree-latin-ext.woff2") format("woff2");
    unicode-range:
      U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304,
      U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020,
      U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }

  /* latin */
  @font-face {
    font-family: "figtree";
    font-weight: 300 900;
    font-style: normal;

    src: url("/public/fonts/figtree-latin.woff2") format("woff2");
    unicode-range:
      U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC,
      U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193,
      U+2212, U+2215, U+FEFF, U+FFFD;
  }
`;
